import React, { useEffect, useMemo } from 'react';

// Components
import Bar from './component/Bar';
import List from './component/List';
import NoData from 'components/NoData';
import Thesaurus from 'pages/Thesaurus';
import Control from './component/Control';
import Loader from 'components/UiKit/Loader';
import ButtonList from 'components/ButtonList';
import HeaderList from './component/HeaderList';
import NotAuthorized from './component/NotAuthorized';
import PricesOnAsking from 'components/PricesOnAsking';
import PaginationView from 'components/PaginationView';
import CustomMessage from 'components/Messages/CustomMessage';

// Lib
import { shallowEqual } from 'react-redux';
import { getValueSize } from 'lib/helpers';
import { useAppSelector } from 'lib/hooks/useDispatch';
import { createTabsConfig, typeBioSearch } from 'pages/Search/utils';

// Hooks
import { useRegisterTutorialKey } from 'lib/hooks/useTutorial';
import { useBioSearchHandler } from 'pages/Search/useHandlers/useSearchBioHandler';

// Constants
import { sizeOptions20 } from 'constans/options';

export const BioSearch: React.FC = () => {
	const registerTutorialKey = useRegisterTutorialKey();

	const {
		dataTree,
		typeTab,
		isNoData,
		authorized,
		loadingBaData,
		structureData,

		toggleFilter
	} = useAppSelector(
		(store) => {
			return {
				dataTree: store.thesaurus.data,
				typeTab: store.thesaurus.typeTab,
				isNoData: store.thesaurus.isNoData,
				loadingBaData: store.thesaurus.loadingBaData,
				structureData: store.thesaurus.structureData,
				authorized: store.settings.user.authorized,

				toggleFilter: store.search.toggleFilter
			};
		},
		shallowEqual
	);

	const {
		goTo,
		paged,
		content,
		currPage,
		pageSize,
		baseBaData
	} = useMemo(() => {
		// @ts-ignore
		const tabData = (structureData && typeTab ? structureData[typeTab] : {}) ?? {};

		return {
			goTo: tabData.goTo ?? 1,
			paged: tabData.paged ?? true,
			content: tabData.content ?? [],
			currPage: tabData.currPage ?? 0,
			pageSize: tabData.pageSize ?? 20,
			baseBaData: tabData.baseBaData ?? []
		};
	}, [structureData, typeTab]);

	const total = Math.ceil(content?.length / (pageSize || 20));

	const {
		handlerSubmitGoTo,
		onPagePress,
		onNextPressLast,
		handlerChangeGoTo,
		onPrevPressLast,
		onKeyPress,
		handleChangeTypeTab,
		handlerChangeSize
	} = useBioSearchHandler(total, content);

	useEffect(() => {
		registerTutorialKey("bio-search", () => !!dataTree?.length, "normal", "general");
		registerTutorialKey("bio-search-list", () => !!content?.length, "normal", "general");
	}, [dataTree, content, registerTutorialKey]);

	const valueSize = getValueSize({
		opt: pageSize,
		options: sizeOptions20
	});

	const newData = useMemo(() => {
		if (paged && content?.length) {
			const start = (pageSize || 20) * currPage;
			const end = start + (pageSize || 20);
			return content.slice(start, end);
		}
		return content;
	}, [content, paged, currPage, pageSize]);

	const configTabs = createTabsConfig({
		isShow: true,
		data: structureData,
		types: typeBioSearch
	});

	if (!authorized) {
		return <>
			<NotAuthorized />
		</>;
	}

	return (
		<div className="search-bio">
			{toggleFilter && <div className="search-bio__filter-mobile">
				<HeaderList
					isModal
					className="search-bio__filter-mobile-header"
				/>
			</div>}

			<div className="search-bio__bg">
				<Bar />
				<Thesaurus />
			</div>
			<Control />

			<ButtonList
				config={configTabs}
				type={typeTab || ''}
				uniqId="bio-search-tabs"
				onClickType={handleChangeTypeTab}
				className="search-screen__btn-tab flex flex-ai-center mt10 flex-direction-column-mobile"
			/>

			{!!baseBaData?.length && <div className="mb10 full-width">
				<PaginationView
					isWhite
					isShowSelect

					goTo={goTo}
					onKeyPress={onKeyPress}
					handlerSubmitGoTo={handlerSubmitGoTo}
					handlerChangeGoTo={handlerChangeGoTo}

					number={currPage}
					totalPages={total}
					onPagePress={onPagePress}
					onNextPressLast={onNextPressLast}
					onPrevPressLast={onPrevPressLast}

					valueSize={valueSize}
					optionsSize={sizeOptions20}
					handlerChangeSize={handlerChangeSize}
				/>
			</div>}
			{loadingBaData
				? <div className="search-bio__bg mt10" style={{ minHeight: '40rem', borderRadius: '1.2rem' }}>
					<Loader type="bio" />
					<p className="search-bio__bio-loader-text text-align-cn">
						Search in progress...
					</p>
				</div>
				: baseBaData.length
					? <div className="search-bio__wrapper-table mt10">
						<HeaderList />
						{content?.length
							? <div className="search-bio__wrapper-table-list">
								<List
									size={pageSize}
									page={currPage}
									data={newData || []}
								/>
							</div>
							: <NoData
								text="No data available"
								className="text-align-cn pb10"
							/>}
					</div>
					: isNoData
						? <div
							style={{ height: '5rem' }}
							className="search-bio__wrapper-table mt10 flex flex-ai-center flex-jc-center"
						>
							<NoData text="No data available" />
						</div> : null}

			{!!content?.length && <div className="flex flex-ai-center flex-jc-between pt15 full-width">
				<PaginationView
					isWhite
					className="full-width"
					children={<PricesOnAsking />}

					goTo={goTo}
					onKeyPress={onKeyPress}
					handlerSubmitGoTo={handlerSubmitGoTo}
					handlerChangeGoTo={handlerChangeGoTo}

					number={currPage}
					totalPages={total}
					onPagePress={onPagePress}
					onNextPressLast={onNextPressLast}
					onPrevPressLast={onPrevPressLast}

					valueSize={valueSize}
					optionsSize={sizeOptions20}
					handlerChangeSize={handlerChangeSize}
				/>
			</div>}

			{!!baseBaData.length && <CustomMessage
				title="Notice"
				className="mt30"
				text="Due to the dynamic change of the Restricted Product list, if the above material is included in the Restricted Product list or regulated as controlled material by the government, we will not sell it."
			/>}
		</div>
	);
};

export default BioSearch;
